import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Check, AlertCircle, ArrowLeft, Download } from 'lucide-react';
import { ROUTES } from '@traction/shared';
import { useAuth } from '@/contexts/AuthContext';
import { createLeadsFromCSV, recordCSVUpload, type CSVLeadInput } from '@traction/api-client';
import { SubscriptionGate } from '@/components/subscription/SubscriptionGate';

type UploadState = 'idle' | 'parsing' | 'preview' | 'uploading' | 'complete' | 'error';

interface ParsedRow {
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  company_name: string;
  company_industry: string;
  company_size: string;
  company_website: string;
  linkedin_url: string;
  location: string;
}

const OPTIONAL_COLUMNS = [
  'first_name',
  'last_name',
  'title',
  'company_name',
  'company_industry',
  'company_size',
  'company_website',
  'linkedin_url',
  'location',
];

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) return { headers: [], rows: [] };

  // Simple CSV parsing (handles basic cases)
  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]).map((h) =>
    h.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
  );
  const rows = lines.slice(1).map(parseLine);

  return { headers, rows };
}

function mapRowToLead(headers: string[], row: string[]): ParsedRow {
  const obj: Record<string, string> = {};
  headers.forEach((header, i) => {
    obj[header] = row[i] || '';
  });

  return {
    first_name: obj.first_name || obj.firstname || obj.name?.split(' ')[0] || '',
    last_name: obj.last_name || obj.lastname || obj.name?.split(' ').slice(1).join(' ') || '',
    email: obj.email || obj.email_address || '',
    title: obj.title || obj.job_title || obj.position || '',
    company_name: obj.company_name || obj.company || obj.organization || '',
    company_industry: obj.company_industry || obj.industry || '',
    company_size: obj.company_size || obj.size || obj.employees || '',
    company_website: obj.company_website || obj.website || obj.url || '',
    linkedin_url: obj.linkedin_url || obj.linkedin || '',
    location: obj.location || obj.city || obj.region || '',
  };
}

export function CSVUploadPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<UploadState>('idle');
  const [fileName, setFileName] = useState('');
  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [results, setResults] = useState<{ created: number; skipped: number; errors: string[] } | null>(null);
  const [error, setError] = useState('');

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setFileName(file.name);
    setState('parsing');
    setError('');

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const { headers, rows } = parseCSV(text);

        if (!headers.some((h) => h.includes('email'))) {
          setError('CSV must contain an email column');
          setState('error');
          return;
        }

        const leads = rows
          .map((row) => mapRowToLead(headers, row))
          .filter((lead) => lead.email.includes('@'));

        if (leads.length === 0) {
          setError('No valid leads found in CSV');
          setState('error');
          return;
        }

        setParsedData(leads);
        setState('preview');
      } catch {
        setError('Failed to parse CSV file');
        setState('error');
      }
    };
    reader.readAsText(file);
  }

  async function handleUpload() {
    if (!user || parsedData.length === 0) return;

    setState('uploading');
    setError('');

    try {
      const leadsToUpload: CSVLeadInput[] = parsedData.map((row) => ({
        first_name: row.first_name,
        last_name: row.last_name,
        email: row.email,
        title: row.title || undefined,
        company_name: row.company_name || undefined,
        company_industry: row.company_industry || undefined,
        company_size: row.company_size || undefined,
        company_website: row.company_website || undefined,
        linkedin_url: row.linkedin_url || undefined,
        location: row.location || undefined,
      }));

      const result = await createLeadsFromCSV(user.uid, leadsToUpload);
      setResults(result);

      await recordCSVUpload(
        user.uid,
        fileName,
        parsedData.length,
        result.created,
        result.errors.length
      );

      setState('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setState('error');
    }
  }

  function handleReset() {
    setState('idle');
    setFileName('');
    setParsedData([]);
    setResults(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  function downloadTemplate() {
    const headers = ['email', 'first_name', 'last_name', 'title', 'company_name', 'company_industry', 'company_size', 'linkedin_url', 'location'];
    const sampleRow = ['john@example.com', 'John', 'Smith', 'CEO', 'Acme Inc', 'SaaS', '11-50', 'https://linkedin.com/in/johnsmith', 'San Francisco, CA'];
    const csv = [headers.join(','), sampleRow.join(',')].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cacao-leads-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <SubscriptionGate feature="csv_upload">
      <div className="mx-auto max-w-3xl px-4 py-8">
        <button
          onClick={() => navigate(ROUTES.LEADS)}
          className="mb-6 flex items-center gap-2 text-sm text-cacao-muted hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Leads
        </button>

        <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-6">
          <h1 className="font-heading text-lg font-medium text-[#F5F5F5]">Import Contacts</h1>
          <p className="mt-1 text-sm text-cacao-muted">
            Upload a CSV file with your existing contacts
          </p>

          {state === 'idle' && (
            <>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="mt-6 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-cacao-dark-tertiary py-12 hover:border-cacao-gold transition-colors"
              >
                <Upload className="h-10 w-10 text-cacao-muted" />
                <p className="mt-3 text-sm font-medium text-[#F5F5F5]">
                  Click to upload or drag and drop
                </p>
                <p className="mt-1 text-xs text-cacao-muted">CSV files only</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <div className="mt-6 flex items-center justify-between rounded-md bg-cacao-dark-tertiary/50 p-4">
                <div>
                  <p className="text-sm font-medium text-[#F5F5F5]">Need a template?</p>
                  <p className="text-xs text-cacao-muted">Download our CSV template with example data</p>
                </div>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-xs font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download Template
                </button>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-medium text-[#F5F5F5]">Supported columns</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {['email (required)', ...OPTIONAL_COLUMNS].map((col) => (
                    <span
                      key={col}
                      className="rounded-full bg-cacao-dark-tertiary px-2 py-1 text-xs text-cacao-muted"
                    >
                      {col}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}

          {state === 'parsing' && (
            <div className="mt-6 flex flex-col items-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
              <p className="mt-4 text-sm text-cacao-muted">Parsing {fileName}...</p>
            </div>
          )}

          {state === 'preview' && (
            <>
              <div className="mt-6 flex items-center gap-3 rounded-md bg-cacao-dark-tertiary/50 p-4">
                <FileText className="h-8 w-8 text-cacao-gold" />
                <div>
                  <p className="text-sm font-medium text-[#F5F5F5]">{fileName}</p>
                  <p className="text-xs text-cacao-muted">{parsedData.length} contacts ready to import</p>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-sm font-medium text-[#F5F5F5]">Preview (first 5 rows)</h3>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-cacao-dark-tertiary">
                        <th className="py-2 pr-4 font-medium text-cacao-muted">Name</th>
                        <th className="py-2 pr-4 font-medium text-cacao-muted">Email</th>
                        <th className="py-2 pr-4 font-medium text-cacao-muted">Title</th>
                        <th className="py-2 font-medium text-cacao-muted">Company</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.slice(0, 5).map((row, i) => (
                        <tr key={i} className="border-b border-cacao-dark-tertiary/50">
                          <td className="py-2 pr-4 text-[#F5F5F5]">
                            {row.first_name} {row.last_name}
                          </td>
                          <td className="py-2 pr-4 text-cacao-muted">{row.email}</td>
                          <td className="py-2 pr-4 text-cacao-muted">{row.title || '-'}</td>
                          <td className="py-2 text-cacao-muted">{row.company_name || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleReset}
                  className="flex-1 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  className="flex-1 rounded-md bg-cacao-gold px-3 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
                >
                  Import {parsedData.length} contacts
                </button>
              </div>
            </>
          )}

          {state === 'uploading' && (
            <div className="mt-6 flex flex-col items-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-cacao-dark-tertiary border-t-cacao-gold" />
              <p className="mt-4 text-sm text-cacao-muted">Importing contacts...</p>
            </div>
          )}

          {state === 'complete' && results && (
            <div className="mt-6">
              <div className="flex items-center gap-3 rounded-md bg-green-500/10 p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/20">
                  <Check className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-[#F5F5F5]">Import complete</p>
                  <p className="text-xs text-cacao-muted">
                    {results.created} created, {results.skipped} skipped (duplicates)
                  </p>
                </div>
              </div>

              {results.errors.length > 0 && (
                <div className="mt-4 rounded-md bg-red-500/10 p-4">
                  <p className="text-sm font-medium text-red-400">
                    {results.errors.length} errors occurred
                  </p>
                  <ul className="mt-2 list-inside list-disc text-xs text-cacao-muted">
                    {results.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-6 flex gap-3">
                <button
                  onClick={handleReset}
                  className="flex-1 rounded-md border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
                >
                  Import More
                </button>
                <button
                  onClick={() => navigate(ROUTES.LEADS)}
                  className="flex-1 rounded-md bg-cacao-gold px-3 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
                >
                  View Leads
                </button>
              </div>
            </div>
          )}

          {state === 'error' && (
            <div className="mt-6">
              <div className="flex items-center gap-3 rounded-md bg-red-500/10 p-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-red-400">Import failed</p>
                  <p className="text-xs text-cacao-muted">{error}</p>
                </div>
              </div>

              <button
                onClick={handleReset}
                className="mt-6 w-full rounded-md border border-cacao-dark-tertiary px-3 py-2 text-sm font-medium text-cacao-muted hover:border-cacao-gold hover:text-white transition-colors"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </SubscriptionGate>
  );
}
